import type { ReactNode } from 'react'
import { forwardRef, useImperativeHandle, useState } from 'react'
import type { SuggestionProps } from '@tiptap/suggestion'
import { useTranslation } from 'next-i18next'
import { twMerge } from 'tailwind-merge'
import { Loader } from 'shared/components/loader'
import type { Suggestion } from 'shared/components/suggest-mention/types'

export interface MentionSuggestionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean
}

export const MentionSuggestionList = forwardRef<
  MentionSuggestionListRef,
  SuggestionProps<Suggestion>
>(function MentionSuggestionList({ items, command, loading }, ref): ReactNode {
  const { t } = useTranslation()
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const index = Math.max(
    0,
    items.findIndex(item => item.userId === selectedId),
  )

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (items.length === 0) return false
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        const delta = event.key === 'ArrowUp' ? -1 : 1
        const next = (index + delta + items.length) % items.length
        setSelectedId(items[next]?.userId ?? null)
        return true
      }
      if (event.key !== 'Enter') return false
      const item = items[index]
      if (!item) return false
      command({ id: item.userId, label: item.displayName, profileImageUrl: item.profileImageUrl })
      return true
    },
  }))

  if (loading && items.length === 0) {
    return (
      <div className="max-h-[250px] w-max max-w-[420px] min-w-[260px] overflow-y-auto rounded-md bg-background-secondary py-1 shadow-mention">
        <div className="flex items-center justify-center py-3.5">
          <Loader type="light" />
        </div>
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className="max-h-[250px] w-max max-w-[420px] min-w-[260px] overflow-y-auto rounded-md bg-background-secondary py-1 shadow-mention">
        <div className="px-4 py-2 text-sm text-text-secondary">
          {t('community.editor.mentions.empty')}
        </div>
      </div>
    )
  }

  return (
    <div className="max-h-[250px] w-max max-w-[420px] min-w-[260px] overflow-y-auto rounded-md bg-background-secondary py-1 shadow-mention">
      {items.map((item, i) => (
        <button
          key={item.userId}
          type="button"
          className={twMerge(
            'flex h-8 w-full cursor-pointer items-center justify-start px-4 text-left',
            i === index
              ? 'bg-text-primary text-background-secondary'
              : 'text-text-secondary hover:bg-text-primary/10',
          )}
          onMouseDown={event => {
            event.preventDefault()
            event.stopPropagation()
            command({
              id: item.userId,
              label: item.displayName,
              profileImageUrl: item.profileImageUrl,
            })
          }}
          onMouseEnter={() => {
            setSelectedId(item.userId)
          }}
        >
          {item.profileImageUrl ? (
            <img
              className="mr-2 size-5 rounded-full"
              width={20}
              height={20}
              src={item.profileImageUrl}
              alt={`Avatar of ${item.displayName}`}
            />
          ) : null}
          <strong className="text-[15px]">{item.displayName}</strong>
        </button>
      ))}
    </div>
  )
})
